import { initAmplify } from "../../_core/lib/amplify";
import { getAuthConfig } from "../api";

import { Fragment } from "react/jsx-runtime";
import { PasswordPolicy } from "../types";
import { z } from "zod";
import { createElement } from "react";
import { ResetPasswordInput } from "aws-amplify/auth";

// Cognito stores email-based usernames lower-cased, so normalize before any auth call to
// avoid "user not found" mismatches when the user types a stray capital.
export const normalizeEmail = (email: string) => email.trim().toLowerCase();

// Builds the resetPassword payload shared by the forgot-password and legacy-reset flows.
export const buildResetPasswordInput = (username: string): ResetPasswordInput => ({
  username,
  options: {
    clientMetadata: {
      portal: "partner",
      // Legacy field kept so older backend deployments still work during rollout. Drop once all envs run the portal-aware backend.
      resetPasswordPageUrl: `${window.location.origin}/reset-password`,
    },
  },
});

export const createPasswordSchema = (passwordPolicy: PasswordPolicy | undefined) => {
  if (passwordPolicy === undefined) {
    return z.object({
      password: z.string(),
      confirmPassword: z.string(),
    });
  }

  let schema = z
    .string()
    .min(passwordPolicy.minimumLength, `Password must be at least ${passwordPolicy.minimumLength} characters long`);

  if (passwordPolicy.requireLowercase) {
    schema = schema.regex(/[a-z]/, "Password must contain at least one lowercase letter");
  }
  if (passwordPolicy.requireUppercase) {
    schema = schema.regex(/[A-Z]/, "Password must contain at least one uppercase letter");
  }
  if (passwordPolicy.requireNumbers) {
    schema = schema.regex(/[0-9]/, "Password must contain at least one number");
  }
  if (passwordPolicy.requireSymbols) {
    schema = schema.regex(
      /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/,
      `Password must contain at least one special character:\n
     ^ $ * . [ ] { } ( ) ? - " ! @ # % & / \\ , > < ' : ; | _ ~ \` + =`
    );
  }

  return z
    .object({
      password: schema,
      confirmPassword: z.string(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: "Passwords must match",
      path: ["confirmPassword"],
    });
};

export const formatMultilineErrorMessage = (message?: string) => {
  if (!message) return null;

  const lines = message.split("\n");

  if (lines.length < 2) return message;

  return createElement(
    Fragment,
    {},
    lines.map((line, i) =>
      createElement(
        Fragment,
        { key: `line-${i}` },
        createElement("span", {}, line),
        i < lines.length - 1 ? createElement("br") : null
      )
    )
  );
};

export async function setupAuth() {
  try {
    const authConfig = await getAuthConfig();
    window.authConfig = authConfig;
    initAmplify(authConfig);

    return;
  } catch (error) {
    throw error;
  }
}
